import { useCallback } from 'react';
import { getFlashcardStats } from '../utils/storage';

const getNodeNeighbors = (node, allNodes, allConns) => {
  const neighbors = [];
  allConns.forEach(conn => {
    if (conn.from === node.id) {
      const child = allNodes.find(n => n.id === conn.to);
      if (child) neighbors.push(child);
    }
    if (conn.to === node.id) {
      const parent = allNodes.find(n => n.id === conn.from);
      if (parent) neighbors.push(parent);
    }
  });
  return neighbors;
};

export function useFlashcardHandlers({
  nodes, connections, updateNode,
  generateFlashcard, generateNodeContent,
  flashcardMode, showFlashcardIcons,
  setFlashcardMode, setShowFlashcardIcons, setRevealedFlashcards, setSelectedNode,
}) {
  const handleToggleFlashcardMode = useCallback(() => {
    setFlashcardMode(!flashcardMode);
    setRevealedFlashcards({});
  }, [flashcardMode, setFlashcardMode, setRevealedFlashcards]);

  const handleToggleFlashcardIcons = useCallback(() => {
    setShowFlashcardIcons(!showFlashcardIcons);
  }, [showFlashcardIcons, setShowFlashcardIcons]);

  const handleToggleFlashcard = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      updateNode(nodeId, {
        isFlashcard: !node.isFlashcard,
        flashcardData: node.isFlashcard
          ? null
          : {
              type: 'cloze',
              front: node.summary || node.title,
              back: node.description || node.title,
              status: 'new'
            }
      });
    }
  }, [nodes, updateNode]);

  const handleRevealFlashcard = useCallback((nodeId) => {
    setRevealedFlashcards(prev => {
      const nextRevealed = !prev[nodeId];
      return { ...prev, [nodeId]: nextRevealed };
    });
    const node = nodes.find(n => n.id === nodeId);
    if (node?.flashcardData?.status === 'new') {
      updateNode(nodeId, { flashcardData: { status: 'revealed' } });
    }
  }, [nodes, setRevealedFlashcards, updateNode]);

  const handleSetFlashcardStatus = useCallback((nodeId, status) => {
    setRevealedFlashcards(prev => ({ ...prev, [nodeId]: true }));
    updateNode(nodeId, { flashcardData: { status } });
    const node = nodes.find(n => n.id === nodeId);
    if (status === 'known' && node) {
      setSelectedNode?.(node);
    }
  }, [nodes, setRevealedFlashcards, updateNode, setSelectedNode]);

  const resolveRootNode = useCallback(async (node) => {
    let rootNode = nodes.find(n => n.type === 'problem');
    if (rootNode && !rootNode.summary && rootNode.id !== node.id) {
      const summary = await generateNodeContent(
        rootNode, getNodeNeighbors(rootNode, nodes, connections)
      );
      if (summary) {
        rootNode = { ...rootNode, summary };
        updateNode(rootNode.id, { summary });
      }
    }
    return rootNode;
  }, [nodes, connections, generateNodeContent, updateNode]);

  const handleGenerateFlashcard = useCallback(async (node, currentSummary) => {
    if (!node) return null;
    try {
      const rootNode = await resolveRootNode(node);
      const flashcard = await generateFlashcard(
        node, getNodeNeighbors(node, nodes, connections), currentSummary, rootNode
      );
      return flashcard;
    } catch (error) {
      console.error('Flashcard generation failed:', error);
      return null;
    }
  }, [nodes, connections, generateFlashcard, resolveRootNode]);

  const handleGenerateContent = useCallback(async (node) => {
    if (!node) return null;
    try {
      const rootNode = await resolveRootNode(node);
      const contentText = await generateNodeContent(
        node, getNodeNeighbors(node, nodes, connections), rootNode
      );
      return contentText;
    } catch (error) {
      console.error('Content generation failed:', error);
      return null;
    }
  }, [nodes, connections, generateNodeContent, resolveRootNode]);

  return {
    handleToggleFlashcardMode,
    handleToggleFlashcardIcons,
    handleToggleFlashcard,
    handleRevealFlashcard,
    handleSetFlashcardStatus,
    handleGenerateFlashcard,
    handleGenerateContent,
    flashcardStats: getFlashcardStats(nodes),
  };
}
